import { PriceBreakdown } from '@/models/registry';
import type { PriceBreakdown as PriceBreakdownType } from '@/models/types';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';

export class PriceBreakdownStore
  implements BaseStore<PriceBreakdown, PriceBreakdownType>
{
  store: Map<string, PriceBreakdown> = new Map();

  storeIndexedByForeignKey: Map<string, PriceBreakdown> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setPriceBreakdownField = <K extends keyof PriceBreakdownType>(
    priceBreakdown: PriceBreakdown,
    key: K,
    value: PriceBreakdownType[K],
  ) => {
    (priceBreakdown as Pick<PriceBreakdown, K>)[key] =
      value as PriceBreakdown[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<PriceBreakdownType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.deal_price_breakdowns.delete(delta.record_id);
      }
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.deal_price_breakdowns.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.deal_price_breakdowns.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.deal_price_breakdowns.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<PriceBreakdownType>;
  }) {
    const existingPriceBreakdown = this.getById(delta.record_id);
    if (!existingPriceBreakdown) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof PriceBreakdownType,
      PriceBreakdownType[keyof PriceBreakdownType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingPriceBreakdown[key] === newValue) {
        continue;
      }

      this.setPriceBreakdownField(existingPriceBreakdown, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingPriceBreakdown);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<PriceBreakdownType>[];
    hydrateIndexedDB?: boolean;
  }): void {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<PriceBreakdownType>;
    hydrateIndexedDB?: boolean;
  }): void {
    if (hydrateIndexedDB) {
      db.deal_price_breakdowns.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    if (delta.action === 'archive') {
      this.remove({ id: delta.record_id, dealId: delta.data.deal_id });
      return;
    }

    this.upsert(delta.data);
  }

  getById(id: string): PriceBreakdown | undefined {
    return this.store.get(id);
  }

  add(input: PriceBreakdownType) {
    const priceBreakdown = new PriceBreakdown(input);
    this.store.set(priceBreakdown.id, priceBreakdown);
    // Index by foreign key for O(1) lookups
    this.storeIndexedByForeignKey.set(priceBreakdown.deal_id, priceBreakdown);
  }

  save(priceBreakdown: PriceBreakdown) {
    this.store.set(priceBreakdown.id, priceBreakdown);
    if (priceBreakdown.deal_id) {
      this.storeIndexedByForeignKey.set(priceBreakdown.deal_id, priceBreakdown);
    }
  }

  upsert(priceBreakdown: PriceBreakdownType) {
    const existingPriceBreakdown = this.getById(priceBreakdown.id);
    if (existingPriceBreakdown) {
      this.replace({ id: priceBreakdown.id, priceBreakdown });
    } else {
      this.add(priceBreakdown);
    }
  }

  replace({
    id,
    priceBreakdown,
  }: {
    id: string;
    priceBreakdown: PriceBreakdownType;
  }) {
    const existingPriceBreakdown = this.getById(id);
    if (!existingPriceBreakdown) return;

    // update the price breakdown
    existingPriceBreakdown.subtotal = priceBreakdown.subtotal;
    existingPriceBreakdown.total = priceBreakdown.total;
    existingPriceBreakdown.sales_tax = priceBreakdown.sales_tax;
    existingPriceBreakdown.registration_fee = priceBreakdown.registration_fee;
    existingPriceBreakdown.documentation_fee = priceBreakdown.documentation_fee;
    existingPriceBreakdown.title_fee = priceBreakdown.title_fee;
    existingPriceBreakdown.delivery_fee = priceBreakdown.delivery_fee;
    existingPriceBreakdown.locked = priceBreakdown.locked;

    this.save(existingPriceBreakdown);
  }

  remove({ id, dealId }: { id: string; dealId: string | null | undefined }) {
    this.store.delete(id);
    db.deal_price_breakdowns.delete(id);
    if (!dealId) return;
    this.storeIndexedByForeignKey.delete(dealId);
  }

  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'deal_price_breakdowns',
      entity: 'deal_price_breakdowns',
      modelStore: priceBreakdownStore,
    });
  }

  async hydrateDeal(dealId: PriceBreakdownType['deal_id']): Promise<void> {
    const items = await db.deal_price_breakdowns.toArray();
    items.forEach((item) => {
      if (item.deal_id === dealId) this.upsert(item);
    });
  }
}

export const priceBreakdownStore = new PriceBreakdownStore();
